import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { styled } from "~/application/theme";
import { Card } from "~/components/Card";
import { EmptyState } from "~/components/EmptyState";
import { Skeleton } from "~/components/Skeleton";
import { ByProductResponse } from "../types";
import { asCurrency, compactCurrency } from "../utils";
import { ChartTooltip } from "./ChartTooltip";

export interface ProductChartProps {
  data?: ByProductResponse;
  isFetching: boolean;
}

const ChartContainer = styled("div", {
  width: "100%",
  height: "320px",
});

const StyledResponsiveContainer = styled(ResponsiveContainer, {
  "& .recharts-cartesian-grid-horizontal, & .recharts-cartesian-grid-vertical": {
    stroke: "$neutral-200",
  },
  "& .recharts-cartesian-axis-tick-value": {
    fill: "$neutrals-dark",
    fontSize: "$xs",
  },
});

// `fill` is a raw SVG attribute on recharts elements and cannot resolve
// Stitches `$token` references — use literal hex values.
const AIRWAY_COLOR = "#0064c5";
const ROAD_COLOR = "#00a878";
const HOTEL_COLOR = "#f5a623";
const VEHICLE_COLOR = "#9b59b6";

const STACK_ID = "products";

export const ProductChart = ({ data, isFetching }: ProductChartProps) => {
  if (isFetching || !data) {
    return (
      <Card css={{ p: "$6" }}>
        <Skeleton variant="text-6" css={{ width: "40%" }} />
        <Skeleton css={{ width: "100%", height: "320px", mt: "$4" }} />
      </Card>
    );
  }

  if (data.series.length === 0) {
    return <EmptyState>Nenhum dado de gasto por produto disponível para o período.</EmptyState>;
  }

  return (
    <Card css={{ p: "$6" }}>
      <ChartContainer>
        <StyledResponsiveContainer width="100%" height="100%">
          <BarChart data={data.series} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="yearMonth" tickLine={false} axisLine={false} tickMargin={8} />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={compactCurrency}
            />
            <Tooltip
              cursor={{ fill: "rgba(228, 228, 231, 0.4)" }}
              content={<ChartTooltip valueFormatter={asCurrency} />}
            />
            <Legend />
            <Bar
              dataKey="airway"
              name="Aéreo"
              stackId={STACK_ID}
              fill={AIRWAY_COLOR}
              maxBarSize={64}
            />
            <Bar
              dataKey="road"
              name="Rodoviário"
              stackId={STACK_ID}
              fill={ROAD_COLOR}
              maxBarSize={64}
            />
            <Bar
              dataKey="hotel"
              name="Hotel"
              stackId={STACK_ID}
              fill={HOTEL_COLOR}
              maxBarSize={64}
            />
            <Bar
              dataKey="vehicle"
              name="Veículo"
              stackId={STACK_ID}
              fill={VEHICLE_COLOR}
              maxBarSize={64}
            />
          </BarChart>
        </StyledResponsiveContainer>
      </ChartContainer>
    </Card>
  );
};

ProductChart.displayName = "ProductChart";
